import { ReactComponent as ChartIcon } from 'assets/icons/chart.svg';
import { ReactComponent as CloudIcon } from 'assets/icons/cloudAI.svg';
import { ReactComponent as FactoryIcon } from 'assets/icons/industriesAI.svg';
import { ReactComponent as NetworkIcon } from 'assets/icons/network.svg';
import { ReactComponent as SupplierRegulationIcon } from 'assets/icons/supplierRegulation.svg';
import { ReactComponent as SpeakerIcon } from 'assets/icons/voice.svg';

import i18n from '../i18n';
import {
  COMPANY_STATUS, EVCAnalysisResultStatus, PILLAR_ACCESS_TYPES, STATUS, VALUE_CHAIN_STATUS,
  VALUE_CHAIN_UNIT_TYPES,
} from './interfaces';

import type IAIAgentsSidebarAgentOption from './interfaces/IAIAgentsSidebarAgentOption';

export const COMPANY_NAME_MIN_LENGTH = 3;
export const COMPANY_NAME_MAX_LENGTH = 64;
export const LESS_THAN_MIN_LENGTH = 1;
export const NAME_MIN_LENGTH = 2;
export const DATA_SETS_NAME_MIN_LENGTH = 1;
export const DATA_SETS_NAME_MAX_LENGTH = 64;
export const NAME_MAX_LENGTH = 50;
export const DESCRIPTION_LENGTH = 300;
export const SELECT_ITEM_HEIGHT = 36;
export const DROPDOWN_ESTIMATED_HEIGHT = 220;
export const NO_LOCATION_KEY = '__no_location__';
export const SMS_TIME_LEFT = 90;
export const MAX_LOGO_SIZE = 5000000;
export const DATE_FORMAT = 'DD/MM/YY';
export const DATE_FORMAT_FULL = 'dd/MM/yyyy';
export const DATA_SETS_DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_FORMAT_ISO = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
export const REPLACE_DOUBLE_SPACE_REGEX = /  +/g;
export const TITLE_DATE_PATTERN = /^(.*?)(\s*\[[^\]]+\])$/;
export const EXTEND_DATE_FORMAT = 'MMMM D, YYYY';
export const VALUE_CHAIN_MAP_ANALYSIS_NAME_PREFIX = 'Untitled Analysis';
export const VALUE_CHAIN_MAP_ANALYSIS_DATE_FORMAT = 'MMM DD, YYYY';
export const VALUE_CHAIN_MAP_ANALYSIS_RESULTS_POLLING_INTERVAL = 10000;
export const ANALYSIS_RESULTS_START_DELAY_MS = 500;
/** Socket event the API sends when a run of an impact analysis changes status. */
export const VALUE_CHAIN_MAP_ANALYSIS_WS_EVENT_RESULT_UPDATED = 'vc.analysis.result.updated';
export const MAX_ANALYSIS_SCENARIOS = 10;
export const MAX_ANALYSIS_TITLE_LENGTH = 100;
export const ANALYSIS_TITLE_FIELD_NAME = 'analysisTitle';

export const ANALYSIS_SIDEBAR = {
  I18N_UNTITLED_ANALYSIS: 'valueChainMap.untitledAnalysis',
  I18N_UNKNOWN_ERROR: 'common.unknownError',
  TAB_SETUP: 'setup',
  TAB_RESULTS: 'results',
  ARIA_MODAL: 'true',
  ROLE_DIALOG: 'dialog',
} as const;
export const DEFAULT_OFFSET = 10;
export const DEFAULT_LIMIT = 50;
export const DEFAULT_MAX_LIMITED = 300;
export const ALPHANUMERIC_SPACES_SPECIAL_CHARTERS_REGEX = /^[A-Za-z0-9\s\-!@#$%^&*()_+={}[\]:;"'<>,.?/\\|]*$/;
export const ALPHANUMERIC_REGEX = /^[a-zA-Z\s-]+$/;
export const ALPHANUMERIC_AND_NUMBER_REGEX = /^[a-zA-Z0-9-]+$/;
export const INTEGER_NUMBER_REGEX = /^\d+$/;
export const TASK_DESCRIPTION_MIN_LENGTH = 600;
export const DEFAULT_ORDER_BY = '-createdAt';
export const LAST_MODIFIED = 'lastModified';
export const UPDATED_AT = 'updatedAt';
export const OPTION_MAX_LENGTH = 100;
export const BOM_SUPPLIER_MAX_LENGTH = 100;
export const BOM_DESCRIPTION_MAX_LENGTH = 500;
export const BOM_ADDITIONAL_INFO_MAX_LENGTH = 50;
export const BOM_INPUT_PRODUCT_REGEX = /^[a-zA-Z0-9- ]+$/;
export const BOM_SOURCE_VALUES = {
  AI: 'ai',
  VC: 'vc',
  VC_POTENTIAL: 'vc_potential',
} as const;
export const BOM_SOURCE_LABELS = {
  AI: 'AI',
  VC: 'Value Chain',
  VC_POTENTIAL: 'Potential',
} as const;
export const BOM_VISIBLE_COLUMNS = {
  CODE: true,
  CATEGORY: true,
  NACE: true,
} as const;

export const BOM_REQUIRED_FIELDS = {
  NAME: 'name',
  CODE: 'code',
  DESCRIPTION: 'description',
  SUPPLIERS: 'suppliers',
  SOURCE: 'source',
  ADDITIONAL_INFO: 'additionalInfo',
} as const;

export type TBomRequiredField = typeof BOM_REQUIRED_FIELDS[keyof typeof BOM_REQUIRED_FIELDS];

export const BOM_SELECT_FIELDS = {
  CATEGORY: 'category',
  NACE_SECTOR: 'naceSector',
} as const;

export type TBomSelectField = typeof BOM_SELECT_FIELDS[keyof typeof BOM_SELECT_FIELDS];

export const PROFIT_CENTER_MODAL_STEPS = {
  STEP_ONE: 1,
  STEP_TWO: 2,
};

export const LOGO_MIME_TYPES_ACCEPT = {
  'image/jpg': [],
  'image/jpeg': [],
  'image/png': [],
};

export const DATA_SETS_MIME_TYPES_ACCEPT = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
  'application/vnd.ms-excel': [],
};

export const PASSWORD_STRENGTH_METER = {
  weak: 15,
  medium: 66,
  strong: 100,
};

export const FILTER_STATUSES = [
  { value: '', label: i18n.t('common.all') },
  { value: COMPANY_STATUS.draft, label: i18n.t('common.draft') },
  { value: COMPANY_STATUS.active, label: i18n.t('common.active') },
  { value: COMPANY_STATUS.deactivated, label: i18n.t('common.deactivated') },
];

export const VALUE_CHAIN_FILTER_STATUSES = [
  { value: '', label: i18n.t('common.all') },
  { value: VALUE_CHAIN_STATUS.current, label: i18n.t('common.current') },
  { value: VALUE_CHAIN_STATUS.inProgress, label: i18n.t('common.inProgress') },
  { value: VALUE_CHAIN_STATUS.readyForReview, label: i18n.t('common.readyForReview') },
  { value: VALUE_CHAIN_STATUS.underReview, label: i18n.t('common.underReview') },
  { value: VALUE_CHAIN_STATUS.requestApproval, label: i18n.t('common.requestApproval') },
  { value: VALUE_CHAIN_STATUS.approved, label: i18n.t('common.approved') },
  { value: VALUE_CHAIN_STATUS.requiresRework, label: i18n.t('common.requiresRework') },
  { value: VALUE_CHAIN_STATUS.archived, label: i18n.t('common.archived') },
];

export const CLIENT_TYPES = [
  { value: 'direct', label: i18n.t('common.direct') },
  { value: 'partner', label: i18n.t('common.partner') },
];

export const STATUS_TYPES = [
  { value: STATUS.enabled, label: i18n.t('common.enabled') },
  { value: STATUS.disabled, label: i18n.t('common.disabled') },
];

export const ACCESS_TYPES = [
  { value: PILLAR_ACCESS_TYPES.visible_enabled, label: i18n.t('common.visibleEnabled') },
  { value: PILLAR_ACCESS_TYPES.dimmed_disabled, label: i18n.t('common.dimmedDisabled') },
  { value: PILLAR_ACCESS_TYPES.invisible_disabled, label: i18n.t('common.invisibleDisabled') },
];

export const AUTHENTICATOR_APP_LINKS = {
  google: {
    apple: 'https://apps.apple.com/us/app/google-authenticator/id388497605',
    google: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en',
  },
  microsoft: {
    apple: 'https://apps.apple.com/us/app/microsoft-authenticator/id983156458',
    google: 'https://play.google.com/store/apps/details?id=com.azure.authenticator&hl=en',
  },
};

export const WEBLATE_LINKS = {
  superAdmin: 'https://onix-systems-weblate-attaima.dev.onix.team/admin/',
  translations: 'https://onix-systems-weblate-attaima.dev.onix.team/projects/',
};

export const EMPTY_SYMBOL = ' ';

export const BLOCKER_STATES = {
  blocked: 'blocked',
  unblocked: 'unblocked',
};

export const API_METHODS = {
  patch: 'PATCH',
  post: 'POST',
  put: 'PUT',
  delete: 'DELETE',
};

/** @deprecated Legacy 3×3 launchpad grid — replaced by workflow hub (see launchpadLanes). */
export const LAUNCHPAD_PILLARS_COUNT = 9;

/** @deprecated Legacy empty placeholder tiles — replaced by lane tiles. */
export const LAUNCHPAD_EMPTY_PILLARS_COUNT = 6;

export const NEW_CLIENT_ID = 'create';

export const SHEET_EXAMPLE_FILES_NAME = {
  profitCenter: 'profit centre.xlsx',
  ownOperation: 'balance sheet assets.xlsx',
  suppliers: 'suppliers.xlsx',
  customers: 'customers.xlsx',
  products: 'products.xlsx',
  bom: 'bill of materials.xlsx',
  supplierDiscovery: 'supplier discovery.xlsx',
  tasks: 'tasks.xlsx',
  questions: 'questions.xlsx',
  disclosures: 'disclosures.xlsx',
};

export const MAPPING_FIELDS_PREFIX = {
  target: 'target.',
  source: 'sources.',
} as const;
export const ZERO_VALUE = 0;
export const MINIMUM_FRACTION_DIGITS = 0;
export const MAXIMUM_FRACTION_DIGITS = 2;
export const DEFAULT_CURRENCY = 'USD';
export const DEFAULT_LOCALE = 'en';

export const THOUSAND_SEPARATOR = ',';
export const DECIMAL_SEPARATOR = '.';
export const DEFAULT_DECIMAL_SCALE = 2;
export const PERCENT_SUFFIX = '%';

export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE',
  PATCH: 'PATCH',
};

export const SOCKET_TRANSPORTS = {
  WEBSOCKET: 'websocket',
} as const;

export const DROPDOWN_DIRECTION = {
  up: 'up',
  down: 'down',
} as const;

export const APPROXIMATE_HEIGHT_OF_DROPDOWN = 300;

export const DEFAULT_PROFIT_CENTER_PERCENTAGE = '100%';

export const REACT_FLOW = {
  MIN_ZOOM: 0.2,
  MAX_ZOOM: 2,
  ZOOM_DURATION: 400,
  PROFIT_CENTER_HEADER_HEIGHT: 40,
  DEFAULT_SPACE_BETWEEN_NODES: 350,
  DRAG_N_DROP_ENOUGH_DISTANCE: 100,
};

export const VALUE_CHAIN_MAP = {
  BOARD_PADDING: 24,
  BOARD_GAP: 32,
  SWIMLANE_WIDTH: 262,
  SWIMLANE_GAP: 197, // 75% of SWIMLANE_WIDTH (262 * 0.75 = 196.5, rounded to 197)
  TITLE_HEIGHT: 80,
  UNIT_PADDING: 28,
  UNIT_TOP_PADDING: 10,
  UNIT_HORIZONTAL_PADDING: 10,
  UNIT_BASIC_HEIGHT: 190,
  UNIT_INPUT_PRODUCT_HEIGHT: 165,
  UNIT_OWN_OPERATION_HEIGHT: 205,
  UNIT_PROFIT_CENTER_HEIGHT: 275,
  UNIT_CUSTOMER_HEIGHT: 170,
  UNIT_SUPPLIER_HEIGHT: 170,
  UNIT_WIDTH: 252,
  UNIT_HEADER_HEIGHT: 42,
  UNIT_VERTICAL_GAP: 24,
  LOGICAL_STATUS_HEIGHT: 34,
  DEFAULT_VIEWPORT: {
    x: 200,
    y: 100,
    zoom: REACT_FLOW.MIN_ZOOM,
  },
  MULTIPLE_EDGE_OFFSET: 15,
  PADDING_CONSTANT: 24,
  UNIT_RECORD_PADDING: 5,
  UNIT_CONTAINER_MARGIN_BOTTOM: 5,
  CORRECTED_PROFIT_CENTER_HEIGHT: 50,
  CORRECTED_INPUT_PRODUCT_HEIGHT: 40,
  UNIT_DEPTH_PADDING: 10,
  STACKED_GROUP_GAP: 50,
  STACKED_GROUP_HEADER_HEIGHT: 48,
  BOARD_INSTANCE_GROUP_HEADER_HEIGHT: 64,
  BOARD_INSTANCE_GROUP_GAP: 16,
  BOARD_INSTANCE_GROUP_HINT_HEIGHT: 32,
  BOARD_INSTANCE_GROUP_MORE_BUTTON_HEIGHT: 44,
};

export const BOARD_INSTANCE_GROUP_VISIBLE_UNITS_LIMIT = 3;

export const BOARD_INSTANCE_GROUP_TYPE = {
  GENERIC: 'generic',
  GEOGRAPHIC: 'geographic',
  ORGANIZATIONAL: 'organizational',
} as const;

export const DEFAULT_BOARD_INSTANCE_GROUP_TYPE = BOARD_INSTANCE_GROUP_TYPE.GENERIC;

export const COUNTRY_GROUP_STACK_ZOOM_THRESHOLD = 0.5;
export const COUNTRY_GROUP_MIN_STACK_SIZE = 4;
export const IS_VC_UNIT_CARD_DETAILS_VISIBLE = false;
export const IS_VC_COUNTRY_GROUPING_ENABLED = false;
export const VC_EDGE_SIMPLE_PATH_ZOOM_THRESHOLD = 0.5;
export const VC_SWIMLANE_TITLE_SCALE_ZOOM_THRESHOLD = 0.5;
export const INITIAL_LAYOUT_STACK_SETTLE_DELAY = 220;
export const INITIAL_LAYOUT_REVEAL_DELAY = 120;
export const INITIAL_FIT_PADDING = 0.06;
export const DEFAULT_SOCKET_FALLBACK_DELAY = 15000;
export const SUPPLIER_DISCOVERY_SOCKET_FALLBACK_DELAY = 10000;
export const AI_PANEL_SELECTABLE_UNIT_TYPES: ReadonlySet<string> = new Set([
  VALUE_CHAIN_UNIT_TYPES.PRODUCT,
  VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT,
]);
/** Any supplier in the Upstream section (all tiers) — shared monitoring agents (reputation, etc.) */
export const AI_PANEL_MONITORING_SELECTABLE_UNIT_TYPES: ReadonlySet<string> = new Set([
  VALUE_CHAIN_UNIT_TYPES.SUPPLIER,
]);
/** Product units for regulation monitoring (Input Product + Output Product lanes) */
export const AI_PANEL_MONITORING_PRODUCT_SELECTABLE_UNIT_TYPES: ReadonlySet<string> = new Set([
  VALUE_CHAIN_UNIT_TYPES.PRODUCT,
  VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT,
]);
/** Climate monitoring — Upstream suppliers + Own Operations + Balance Sheet Assets */
export const AI_PANEL_MONITORING_CLIMATE_SELECTABLE_UNIT_TYPES: ReadonlySet<string> = new Set([
  VALUE_CHAIN_UNIT_TYPES.SUPPLIER,
  VALUE_CHAIN_UNIT_TYPES.OWN_OPERATION,
  VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET,
]);
export const MIN_SWIMLANE_BOTTOM_GAP = 20;
export const VALUE_CHAIN_STACKED_GROUP_REVEAL_COUNT = 5;
export const VALUE_CHAIN_UNIT_SIBLING_PADDING = 40;
export const VALUE_CHAIN_LOGICAL_UNIT_SIBLING_PADDING = 50;

export const VALUE_CHAIN_AI_AGENT = {
  SESSION_TYPE: 'n-tier-discovery',
  SUPPLIER_DISCOVERY_SESSION_TYPE: 'supplier-discovery',
  BILL_OF_MATERIALS_SESSION_TYPE: 'bill-of-materials',
  WS_EVENT_AGENT_SESSION_UNIT_UPDATED: 'vc.agent.session.unit.updated',
  WS_EVENT_AGENT_SESSION_UPDATED: 'vc.agent.session.updated',
  UNIT_NODE_PREFIX: 'unit_',
  STATUS_UNIT_SELECTION: 'unit-selection',
  STATUS_DONE: 'done',
  STATUS_IN_PROGRESS: 'in_progress',
  STATUS_IN_PROGRESS_LEGACY: 'in-progress',
  STATUS_PROCESSING: 'processing',
  STATUS_EMPTY: 'empty',
  CARD_PANEL_SYNC_DELAY: 1500,
  GENERATING_MODAL_POLLING_INTERVAL: 15000,
  COMPLETE_BOM_ITEMS_COUNT: 'completed',
  STATUS_REVIEW: 'review',
  AI_STATUS: 'processing',
} as const;

/** Monitoring agents (reputation, regulation, finance, climate, ownership) — separate from N-Tier */
export const VALUE_CHAIN_MONITORING_AGENT = {
  REPUTATION_SESSION_TYPE: 'reputation-monitoring',
  MEDIA_SENTIMENT_SUB_TYPE: 'media-sentiment',
  REGULATION_SESSION_TYPE: 'regulation-monitoring',
  REGULATION_TRACKING_SUB_TYPE: 'regulation-tracking',
  FINANCE_SESSION_TYPE: 'finance-monitoring',
  FINANCIAL_RISK_SUB_TYPE: 'financial-risk',
  OWNERSHIP_SESSION_TYPE: 'ownership-monitoring',
  /** Nest subType after ATT-109 (was industrial-forecasting). */
  OWNERSHIP_MONITORING_SUB_TYPE: 'ownership-monitoring',
  CLIMATE_SESSION_TYPE: 'climate-monitoring',
  CLIMATE_RISK_SUB_TYPE: 'climate-risk',
  SENTIMENT_RISK_THRESHOLD: 40,
  REPUTATION_MONITORING_ENABLED: true,
  /** ATT-136: live session start. When true, start failures mock processing → done. */
  REPUTATION_MONITORING_USE_MOCK: false,
  /** Phase 1 enables the Product Regulation sidebar card. */
  REGULATION_MONITORING_ENABLED: true,
  REGULATION_MONITORING_USE_MOCK: false,
  /** Phase 1 enables the Financial Stability sidebar card (ATT-59). */
  FINANCE_MONITORING_ENABLED: true,
  FINANCE_MONITORING_USE_MOCK: false,
  /** Phase 1 enables the Ownership Monitoring sidebar card (ATT-61). */
  OWNERSHIP_MONITORING_ENABLED: true,
  OWNERSHIP_MONITORING_USE_MOCK: false,
  /** Phase 1 enables Climate Monitoring (replaces natural-disaster Coming soon). */
  CLIMATE_MONITORING_ENABLED: true,
  CLIMATE_MONITORING_USE_MOCK: false,
} as const;

/**
 * Legacy in-progress variants are kept alongside the enum value the backend now sends.
 * @deprecated
*/
export const VALUE_CHAIN_MAP_ANALYSIS_RESULT_PROCESSING_STATUSES: ReadonlySet<string> = new Set([
  EVCAnalysisResultStatus.PROCESSING,
  VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS,
  VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS_LEGACY,
]);

export const getAIAgentsSidebarAgentOptions = ({
  onSelectSupplierNTier,
  onSelectReputationMonitoring,
  onSelectRegulationMonitoring,
  onSelectFinanceMonitoring,
  onSelectOwnershipMonitoring,
  onSelectClimateMonitoring,
  hasSupplierNTierAttention,
  hasReputationMonitoringAttention,
  hasRegulationMonitoringAttention,
  hasFinanceMonitoringAttention,
  hasOwnershipMonitoringAttention,
  hasClimateMonitoringAttention,
}: {
  onSelectSupplierNTier: () => void,
  onSelectReputationMonitoring: () => void,
  onSelectRegulationMonitoring: () => void,
  onSelectFinanceMonitoring: () => void,
  onSelectOwnershipMonitoring: () => void,
  onSelectClimateMonitoring: () => void,
  hasSupplierNTierAttention: boolean,
  hasReputationMonitoringAttention: boolean,
  hasRegulationMonitoringAttention: boolean,
  hasFinanceMonitoringAttention: boolean,
  hasOwnershipMonitoringAttention: boolean,
  hasClimateMonitoringAttention: boolean,
}): IAIAgentsSidebarAgentOption[] => [
  {
    id: 'supplier-n-tier-discovery',
    titleKey: 'valueChainMap.aiAgentsSidebarSupplierDiscoveryTitle',
    descriptionKey: 'valueChainMap.aiAgentsSidebarSupplierDiscoveryDescription',
    Icon: NetworkIcon,
    disabled: false,
    onClick: onSelectSupplierNTier,
    showAttentionBadge: hasSupplierNTierAttention,
  },
  {
    id: 'product-regulation-monitoring',
    titleKey: 'valueChainMap.aiAgentsSidebarProductRegulationTitle',
    descriptionKey: 'valueChainMap.aiAgentsSidebarProductRegulationDescription',
    Icon: SupplierRegulationIcon,
    disabled: !VALUE_CHAIN_MONITORING_AGENT.REGULATION_MONITORING_ENABLED,
    showComingSoon: !VALUE_CHAIN_MONITORING_AGENT.REGULATION_MONITORING_ENABLED,
    onClick: onSelectRegulationMonitoring,
    showAttentionBadge: hasRegulationMonitoringAttention,
  },
  {
    id: 'public-sentiment-monitoring',
    titleKey: 'valueChainMap.aiAgentsSidebarPublicSentimentTitle',
    descriptionKey: 'valueChainMap.aiAgentsSidebarPublicSentimentDescription',
    Icon: SpeakerIcon,
    disabled: !VALUE_CHAIN_MONITORING_AGENT.REPUTATION_MONITORING_ENABLED,
    showComingSoon: !VALUE_CHAIN_MONITORING_AGENT.REPUTATION_MONITORING_ENABLED,
    onClick: onSelectReputationMonitoring,
    showAttentionBadge: hasReputationMonitoringAttention,
  },
  {
    id: 'financial-stability-monitoring',
    titleKey: 'valueChainMap.aiAgentsSidebarFinancialStabilityTitle',
    descriptionKey: 'valueChainMap.aiAgentsSidebarFinancialStabilityDescription',
    Icon: ChartIcon,
    disabled: !VALUE_CHAIN_MONITORING_AGENT.FINANCE_MONITORING_ENABLED,
    showComingSoon: !VALUE_CHAIN_MONITORING_AGENT.FINANCE_MONITORING_ENABLED,
    onClick: onSelectFinanceMonitoring,
    showAttentionBadge: hasFinanceMonitoringAttention,
  },
  {
    id: 'climate-monitoring',
    titleKey: 'valueChainMap.aiAgentsSidebarClimateMonitoringTitle',
    descriptionKey: 'valueChainMap.aiAgentsSidebarClimateMonitoringDescription',
    Icon: CloudIcon,
    disabled: !VALUE_CHAIN_MONITORING_AGENT.CLIMATE_MONITORING_ENABLED,
    showComingSoon: !VALUE_CHAIN_MONITORING_AGENT.CLIMATE_MONITORING_ENABLED,
    onClick: onSelectClimateMonitoring,
    showAttentionBadge: hasClimateMonitoringAttention,
  },
  {
    id: 'ownership-monitoring',
    titleKey: 'valueChainMap.aiAgentsSidebarOwnershipMonitoringTitle',
    descriptionKey: 'valueChainMap.aiAgentsSidebarOwnershipMonitoringDescription',
    Icon: FactoryIcon,
    disabled: !VALUE_CHAIN_MONITORING_AGENT.OWNERSHIP_MONITORING_ENABLED,
    showComingSoon: !VALUE_CHAIN_MONITORING_AGENT.OWNERSHIP_MONITORING_ENABLED,
    onClick: onSelectOwnershipMonitoring,
    showAttentionBadge: hasOwnershipMonitoringAttention,
  },
];

export const TABLE_COLUMNS_COUNT = 10;
export const TAB_SCROLL_OFFSET = 240;
export const REVIEW_TRANSITION_DELAY_MS = 1000;
export const BOM_TABLE_COLUMN_WIDTHS = [
  48,
  156,
  110,
  156,
  140,
  212,
  160,
  184,
  112,
  64,
] as const;
export const CATEGORY_OPTIONS = [
  { value: 'auxiliary-material', label: 'Auxiliary Material' },
  { value: 'chemicals', label: 'Chemicals' },
  { value: 'consumables', label: 'Consumables' },
  { value: 'direct-material', label: 'Direct Material' },
  { value: 'energy', label: 'Energy' },
  { value: 'finished-goods', label: 'Finished Goods' },
];

export const VALUE_CHAIN_MAP_CONNECTION_COLORS = {
  LOGICAL: '#467AFF',
  SUPPLIERS_OF_GOODS: '#07BD2C',
  FINANCIAL: '#121113',
  SERVICE: '#FC9709',
  MANUFACTURE: '#BE84FF',
};
export const DISABLING_REASON_MIN_LENGTH = 3;
// Mirrors the backend's MinLength(3) + MaxLength(256) on every value chain connection comment.
export const CONNECTION_COMMENT_MIN_LENGTH = 3;
export const CONNECTION_COMMENT_MAX_LENGTH = 256;

export const LINK_TYPES = {
  linked: 'linked',
  unlinked: 'unlinked',
  all: 'all',
};

export const PROPERTY_TYPES = {
  businessUnitType: 'businessUnitType',
};

export const PROPERTY_VALUES = {
  logic: 'Logic',
  logical: 'Logical',
};

export const MENU_POSITION = {
  ABSOLUTE: 'absolute',
  FIXED: 'fixed',
} as const;

export const ERROR_CODES = {
  DUPLICATE_ERROR: '3.8.2',
  DUPLICATE_ERROR_UNIQUE_KEY: '3.8.2.2',
} as const;

export const VIRTUAL_PROPERTY_STATUS = {
  CALCULATING: 'calculating',
} as const;

// eslint-disable-next-line no-template-curly-in-string
export const VALUE_PATTERN = '${value}';

// eslint-disable-next-line no-template-curly-in-string
export const VALUES_PATTERN = '${values}';

export const TAB_TYPES = {
  VALUE_CHAIN: 'valueChain',
  MATERIALITY_ASSESSMENT: 'materialityAssessment',
} as const;

export const DEFAULT_OPTION = {
  label: '',
  value: '',
} as const;

export const DEFAULT_OPTION_OPPORTUNITY = {
  label: 'Positive',
  value: 'positive',
} as const;

export const DEFAULT_OPTION_ALL = {
  label: 'All',
  value: '',
} as const;

export const TASK_RELATED_TO_OPTIONS = [
  'Value Chain - Operation Swift', 'Value Chain - Competitor',
  'Value Chain - Operation Payment', 'Materiality Assessment -  Operation Swift',
  'Materiality Assessment -  Competitor', 'Materiality Assessment -  Operation Swift2',
  'Materiality Assessment -  Competitor2',
];

export const TASK_STATUS_OPTIONS = [
  'created', 'assigned', 'in-progress', 'completed', 'deferred', 'blocked', 'cancelled',
];

export const VC_STATUS_OPTIONS = [
  'current', 'in-progress', 'ready-for-review', 'under-review',
  'request-approval', 'approved', 'requires-rework', 'archived',
];

export const TASK_TABS = {
  ACTIVITIES: 'activities',
  COMMENTS: 'comments',
} as const;

export const TASK_ACTIVITIES_CATEGORIES = [
  {
    label: 'activities',
    value: 'activity',
  },
  {
    label: 'comments',
    value: 'comment',
  },
] as const;

export const TASK_RELATED_TO_FIRST_ITEM = {
  id: 0,
  name: 'Value Chain - Settings',
  created: {
    date: '',
    byName: '',
  },
  lastModified: {
    date: '',
    byName: '',
  },
  status: '',
};

export const TASK_RELATED_TO_FIRST_ITEM_MA = {
  id: 0,
  name: 'Materiality Assessment - Settings',
  status: '',
  createdAt: '',
  updatedAt: '',
  createdBy: {
    id: 0,
    firstName: '',
    lastName: '',
    email: '',
  },
  updatedBy: {
    id: 0,
    firstName: '',
    lastName: '',
    email: '',
  },
};

export const TASK_ATTACHMENT_ALLOWED_TYPES = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.png', '.jpg', '.jpeg'];

export const TASK_ATTACHMENT_ALLOWED_TYPES_STRING = '.pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg';

export const TASK_ATTACHMENT_MAX_FILE_SIZE = 20971520;

export const TYPE_RELATE_TO = {
  VALUE_CHAIN: 'Value Chain - Settings',
  MATERIALITY_ASSESSMENT: 'Materiality Assessment - Settings',
} as const;

export const TASK_SORT_BY = {
  NAME: 'name',
  TITLE: 'title',
  CREATED: 'created',
  UPDATED: 'updated',
  CREATED_AT: 'createdAt',
  LAST_MODIFIED: '-createdAt',
} as const;

export const TASK_TYPE_ACTION = {
  CREATE: 'create',
  UPDATE: 'update',
  EDIT: 'edit',
} as const;

export const TASK_TYPE = {
  VALUE_CHAIN: 'Value Chain',
  MATERIALITY_ASSESSMENT: 'Materiality Assessment',
  VCM: 'vcm',
  MA: 'ma',
} as const;

export const TASK_MANAGEMENT_PILLAR_KEY = 'task-management/task-management';

export const COMMENTS_TYPES = {
  UPDATE: 'update',
  CREATE: 'create',
  DELETE: 'delete',
  ATTACHMENT_ADDED: 'attachment-added',
  ATTACHMENT_REMOVED: 'attachment-removed',
  COMMENT: 'comment',
} as const;

export const NOTIFICATION_TABS = {
  ALL: 'all',
  UNREAD: 'unread',
} as const;

export const DATE_FORMATTING = {
  ONE_DAY_AGO: '1 day ago',
  DAYS_AGO: 'days ago',
} as const;

export const DATE_LOCALE = 'en-US';

export const DATE_FORMAT_OPTIONS = {
  FULL_DATE: {
    month: 'long' as const,
    day: 'numeric' as const,
    year: 'numeric' as const,
  },
  MONTH_YEAR: {
    month: 'long' as const,
    year: 'numeric' as const,
  },
} as const;

export const DATE_CALCULATIONS = {
  MILLISECONDS_IN_DAY: 1000 * 60 * 60 * 24,
  MAX_DAYS_FOR_RELATIVE_FORMAT: 5,
  ONE_DAY: 1,
  DEFAULT_TIMES: 0,
} as const;

export const TASK_MANAGEMENT_PERMISSIONS = {
  VIEW_ONLY: 'view-only',
  VIEW_EDIT: 'view-edit',
  VIEW_AUDIT: 'view-audit',
  SIGN_OFF: 'sign-off',
  NO_ACCESS: 'no-access',
} as const;

export const DEFAULT_OPTION_VC = {
  label: 'Value Chain - Settings',
  value: '',
} as const;

export const DEFAULT_OPTION_MA = {
  label: 'Materiality Assessment - Settings',
  value: '',
} as const;
export const DEFAULT_VC_COUNT = 5;

export const DEFAULT_ITEM = { id: undefined, firstName: undefined, lastName: undefined };

export const OPTIONS_QUESTIONS_MENU = ['importFile', 'createNew'];

export const MODAL_QUESTIONS = {
  IMPORT_FILE: 'importFile',
  CREATE_NEW: 'createNew',
  UPLOAD_FILE: 'uploadFile',
} as const;

export const MATERIALITY_ASSESSMENT_STATUS_OPTIONS = [
  'draft', 'approved', 'in_progress', 'ready_for_review',
];

export const MATERIALITY_ASSESSMENT_STATUS = {
  DRAFT: 'draft',
  APPROVED: 'approved',
  IN_PROGRESS: 'in_progress',
  READY_FOR_REVIEW: 'ready_for_review',
  PENDING_REVIEW: 'pending_review',
};

export const OPTION_CATEGORY_INVESTOR = [
  {
    value: 'A',
    label: 'Class A',
  },
  {
    value: 'B',
    label: 'Class B',
  },
  {
    value: 'C',
    label: 'Class C',
  },
  {
    value: 'D',
    label: 'Class D',
  },
];

export const OPTION_CATEGORY_BOARD = [
  {
    value: 'executive_direction',
    label: 'Executive Direction',
  },
  {
    value: 'non_executive',
    label: 'Non Executive',
  },
  {
    value: 'secretary',
    label: 'Secretary',
  },
];

export const OPTION_PROFESSION_BOARD = [
  {
    value: 'finance',
    label: 'Finance',
  },
  {
    value: 'hr',
    label: 'HR',
  },
  {
    value: 'legal',
    label: 'Legal',
  },
  {
    value: 'egs',
    label: 'EGS',
  },
  {
    value: 'etc',
    label: 'Etc',
  },
];

export const OPTION_ROLE_EMPLOYEES = [
  {
    value: 'administrator',
    label: 'Administrator',
  },
  {
    value: 'hr',
    label: 'HR',
  },
  {
    value: 'legal',
    label: 'Legal',
  },
  {
    value: 'finance',
    label: 'Finance',
  },
];

export const OPTION_CATEGORY_COMMUNITY = [
  {
    value: 'environment',
    label: 'Environment',
  },
  {
    value: 'health',
    label: 'Health',
  },
  {
    value: 'disability',
    label: 'Disability',
  },
  {
    value: 'etc',
    label: 'Etc.',
  },
];

export const OPTION_CATEGORY_NOGS = [
  {
    value: 'environment',
    label: 'Environment',
  },
  {
    value: 'health',
    label: 'Health',
  },
  {
    value: 'disability',
    label: 'Disability',
  },
  {
    value: 'etc',
    label: 'Etc.',
  },
];

export const GEO_TOGGLE_NAME = 'geo-toggle';
export const FOLLOW_CONNECTIONS_TOGGLE_NAME = 'follow-connections-toggle';
export const TOPIC_REFINEMENT_NAME = 'topic-refinement';

export const OPTION_ANALYSIS_FILTER = [
  {
    value: '',
    label: 'All',
  },
  {
    value: 'suppliers',
    label: 'Suppliers',
  },
  {
    value: 'customers',
    label: 'Customers',
  },
  {
    value: 'investors',
    label: 'Investors',
  },
];

export const OPTION_IRO_IDENTIFICATION_FILTER = [
  'accommodation_and_food_services',
  'agriculture_and_fishing',
  'construction',
  'distribution_and_trade',
  'energy',
  'financial_services',
  'health_sciences',
  'ict',
  'manufacturing',
  'mining_and_quarrying',
  'real_estate',
  'transportation_and_storage_services',
  'water_and_waste_management',
];

export const TABS_IRO_IDENTIFICATION = [
  { id: 1, name: 'Impacts', value: 'impacts' },
  { id: 2, name: 'Risks', value: 'risks' },
  { id: 3, name: 'Opportunity', value: 'opportunity' },
  { id: 4, name: 'Status', value: 'status' },
  { id: 5, name: 'Assign', value: 'assign' },
];

export const TABS_IRO_SEND_QUESTIONARY = [
  { id: 1, name: 'Impacts', value: 'impacts' },
  { id: 2, name: 'Risks', value: 'risks' },
  { id: 3, name: 'Opportunity', value: 'opportunity' },
];

export const MODALS_IRO_IDENTIFICATION = {
  ADD_NEW_ITEM: 'add-new-item',
  ADD_CUSTOM_ITEM: 'add-custom-item',
  ADD_IMPACT: 'impact',
  ADD_RISKS: 'risks',
  ADD_OPPORTUNITY: 'opportunity',
  ADD_NEW_EMAIL: 'add-new-email',
  SEND_EMAILS: 'send-emails',
};

export const IMPACT_STATUS = {
  POSITIVE: 'positive',
  NEGATIVE: 'negative',
};

export const IMPACT_STATUS_OPTIONS = [
  {
    value: 'positive',
    label: 'Positive',
  },
  {
    value: 'negative',
    label: 'Negative',
  },
];

export const IRO_VC_POSITION_OPTIONS = [
  {
    value: 'Upstream',
    label: 'Upstream',
  },
  {
    value: 'Own Operations',
    label: 'Own Operations',
  },
  {
    value: 'Downstream',
    label: 'Downstream',
  },
];

export const IRO_CATEGORY_OPTIONS = [
  { value: 'actual', label: 'Actual' },
  { value: 'potential', label: 'Potential' },
];

export const CATEGORY_IMPACT_TEMPLATE = {
  IMPACT: 'impact',
  RISK: 'risk',
  OPPORTUNITY: 'opportunity',
  TEMPLATE: 'template',
  HINT_TEMPLATE: 'hintTemplate',
};

export const TABS_IRO_IDENTIFICATION_ADMIN = [
  { id: 1, name: 'Impacts', value: 'impacts' },
  { id: 2, name: 'To Approve', value: 'toApprove' },
];

export const REQUEST_STATUSES = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  DRAFT: 'draft',
};

export const IRO_CATEGORY_TEMPLATE = ['impact', 'risk', 'opportunity'];

export const ESTIMATED_VALUE_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

export const STATE_RISKS = {
  AFFECTED_UP_TO_DATE: 'affectedUpToDate',
  GLOBAL_RISKS_UP_TO_DATE: 'globalRisksUpToDate',
};

export const INITIATE = 'initiate';

export const DEFAULT_OPTION_TOPICS = {
  id: 0,
  label: 'All',
  value: '',
  name: '',
};

export const QUESTIONNAIRE_STATUS = {
  SUCCESS: 'success',
  PENDING: 'pending',
  FAILED: 'failed',
  IN_PROGRESS: 'in_progress',
};

export const SORT_BY_OPTIONS_EXCLUDE = {
  ACTIONS: 'actions',
  ACTION: 'action',
  SOURCE: 'source',
  INDUSTRY_CODE: 'industryCode',
};

export const VALUE_CHAIN_STATUS_DEFAULT = ['ready-for-review', 'under-review',
  'request-approval', 'approved', 'requires-rework', 'current', 'in-progress'];

export const CONNECTED_FILTERS = {
  connections: ['logical', 'service', 'supplierOfGoods'],
};

export const COLUMN_LABELS = {
  QUESTION: 'Question',
  QUESTION_TYPE: 'Question Type',
  TYPE: 'type',
  TEXT: 'text',
};

export const VC_BOARD_TYPES = {
  OWN_OPERATIONS: 'Own Operations',
  UPSTREAM: 'Upstream',
  DOWNSTREAM: 'Downstream',
};

export const VALUE_CHAIN_POSITION_DOWNSTREAM = 'Downstream';

export const MODAL_REFINEMENT_QUESTIONS = {
  QUESTIONS: 'questions',
  DISCLOSURES: 'disclosures',
  REGIONS: 'regions',
  COMPANIES: 'companies',
};

export const USER_ROLES = {
  COMPANY_ADMIN: 'company-admin',
  SIGNATORY: 'signatory',
};

export const TYPE_OPTIONS = {
  ACTUAL: 'actual',
  POTENTIAL: 'potential',
} as const;

export const KEY_PERMISSIONS = {
  MATERIALITY_ASSESSMENT_MANAGEMENT: 'materiality-assessment/materiality-assessment-management',
} as const;
